import { Component, Inject, OnInit } from "@angular/core";
import { LinkedLoginAttemptKey, OktaAuthService } from "../../../services/okta-auth.service";
import { Capacitor } from '@capacitor/core';
import { UpdateCheckService } from "../../../services/mobile-app-update-check-service";
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CommonModule } from '@angular/common';
import { UserService } from "../../../services/user-service.service";
import { Router } from "@angular/router";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LoginDebugService } from "../../../services/login-debug.service";
import { DeepLinkService } from "../../../services/deep-link.service";
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ENVIRONMENT } from "../../../services/environment.token";
import { PARENT_RELATIONS_PHONE_DISPLAY, PARENT_RELATIONS_PHONE_TEL } from "../../apps/shared/nha-contact-constants";

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    imports: [CommonModule, MatProgressSpinner, MatSlideToggle]
})
export class HomeComponent implements OnInit {
  readonly parentRelationsPhoneDisplay = PARENT_RELATIONS_PHONE_DISPLAY;
  readonly parentRelationsPhoneTel = PARENT_RELATIONS_PHONE_TEL;

  isMobile: boolean = Capacitor.isNativePlatform();
  isWebView: boolean = !this.isMobile;
  currentYear: number = new Date().getFullYear();
  selectedLoginType: string | null = null; // Track the selected login type
  showAndroidStoreLink: boolean = true;
  showIosStoreLink: boolean = true;
  capgoBundleVersion: string = '';
  isLoggingIn: boolean = false; // Track login loading state
  private destroyed = false;
  showLoginDebugToggle: boolean = false;

  constructor(
    private oktaAuthService: OktaAuthService,
    private mobileUpdateCheckService: UpdateCheckService,
    private userService: UserService,
    private router: Router,
    private hybridStorageService: HybridStorageService,
    private deepLinkService: DeepLinkService,
    public loginDebugService: LoginDebugService,
    @Inject(ENVIRONMENT) private environment: any
  ) {
    this.showLoginDebugToggle = this.environment.showLoginDebugToggle && this.isMobile;

    // If toggle is hidden and debug mode is enabled, disable it
    if (!this.showLoginDebugToggle && this.loginDebugService.isDebugModeEnabled) {
      this.loginDebugService.setDebugMode(false);
    }
  }

  async ngOnInit(): Promise<void> {
    // Get Capgo bundle version if on native platform
    if (this.isMobile) {
      try {
        const current = await CapacitorUpdater.current();
        this.capgoBundleVersion = current.bundle.version || '';
      } catch (error) {
        console.error('Error getting Capgo bundle version:', error);
      }
    }

    // Wait for tokens to be initialized before checking auth - prevents race condition
    // where component checks before app.component finishes token sync from storage
    await this.oktaAuthService.waitForTokensInitialized();

    // Check if user is already authenticated and set profile if needed
    const isAuthed = await this.oktaAuthService.getOktaAuth().isAuthenticated();

    if (isAuthed) {
      try {
        await this.userService.setUserProfile(); // Fetch user profile if authenticated

        // For mobile: navigate to pending deeplink if present, otherwise mainmenu
        // For web: AppComponent handles the redirect
        if (this.isMobile && !this.mobileUpdateCheckService.isCriticalUpdateAvaliable && !this.destroyed) {
          const pendingDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
          if (this.destroyed) return; // Check again after async storage read
          if (pendingDeepLink) {
            await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
            this.router.navigateByUrl(pendingDeepLink);
          } else if (!this.deepLinkService.deepLinkPending) {
            // A short-link deep link is still being resolved by DeepLinkService — don't bounce to
            // /mainmenu and stomp the navigation it's about to perform once it resolves.
            this.router.navigate(['/mainmenu']);
          }
        }
      } catch (error) {
        console.error('[HomeComponent] Error setting user profile:', error);
      }
    } else if (!this.isMobile) {
      const linkedLoginType = await this.takeLoginTypeFromLink();
      if (linkedLoginType) {
        await this.onLoginTypeSelected(linkedLoginType);
      }
    }
  }

  /**
   * Reads the loginType query parameter other NHA apps add when they link here (the mirror of
   * Scholar's inbound handling), naming which Okta configuration to sign in with so an arriving
   * user skips the account-type picker. With a live shared Okta session the redirect completes
   * silently; without one the user sees the normal Okta login page for that type. Web only, with
   * a short per-tab cooldown so an abandoned login doesn't loop back through the redirect.
   */
  private async takeLoginTypeFromLink(): Promise<string | null> {
    const attemptCooldownMs = 60000;

    try {
      const lastAttempt = Number(sessionStorage.getItem(LinkedLoginAttemptKey));
      if (lastAttempt && Date.now() - lastAttempt < attemptCooldownMs) {
        return null;
      }
    } catch {
      return null;
    }

    let requested = new URLSearchParams(window.location.search).get('loginType');

    if (!requested) {
      // The guard may have bounced here from a deep link, preserving the original URL in storage.
      const pending = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
      if (pending) {
        requested = new URLSearchParams(pending.split('?')[1] ?? '').get('loginType');
      }
    }

    if (!requested) {
      return null;
    }

    const loginType = requested.charAt(0).toUpperCase() + requested.slice(1).toLowerCase();
    if (loginType !== 'Parent' && loginType !== 'Student' && loginType !== 'Staff') {
      return null;
    }

    try {
      sessionStorage.setItem(LinkedLoginAttemptKey, String(Date.now()));
    } catch {}

    return loginType;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  async onDebugModeToggle(event: MatSlideToggleChange): Promise<void> {
    await this.loginDebugService.setDebugMode(event.checked);
    console.warn('[HomeComponent] Login debug mode:', event.checked ? 'enabled' : 'disabled');
  }

  async onLoginTypeSelected(loginType: string): Promise<void> {
    // Show loading spinner immediately
    this.isLoggingIn = true;
    this.selectedLoginType = loginType;

    try {
      // Update Okta configuration based on login type
      this.oktaAuthService.updateOktaConfigForLoginType(loginType);

      // Get the updated Okta auth instance
      const updatedOktaAuth = this.oktaAuthService.getOktaAuth();

      const isCriticalUpdateAvailable = await this.mobileUpdateCheckService.checkForUpdate();

      if (this.isMobile && !isCriticalUpdateAvailable) {
        this.oktaAuthService.getOktaAuth().tokenManager.clear();
        this.oktaAuthService.setIsLoggingIn(true);
        await updatedOktaAuth.signInWithRedirect({
          redirectUri: await this.oktaAuthService.getRedirectUriForLoginType()
        });
        setTimeout(() => this.isLoggingIn = false, 10000);
      } else {
        await updatedOktaAuth.signInWithRedirect();
        this.isLoggingIn = false;
      }
    } catch (error) {
      console.error('Error during login:', error);
      this.isLoggingIn = false;
    }
  }
}
